import type * as React from 'react';
import { Drawer } from '@base-ui/react/drawer';
import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn.ts';
import { SectionLabel } from '../section-label/section-label.tsx';

/*
 * The pages behind one tab bar tile, sliding up from the bottom. Base UI's
 * drawer, so focus trap, Escape and backdrop tap come from its dialog, and a
 * flick down puts it away.
 */
export const MobileSheet = Drawer.Root;

export interface MobileSheetContentProps extends Omit<
  Drawer.Popup.Props,
  'className' | 'style' | 'title'
> {
  /* What the sheet is a list of: its heading, and the dialog's accessible name. */
  title: React.ReactNode;
  closeLabel?: string;
  className?: string;
}

export function MobileSheetContent({
  title,
  closeLabel = 'Close',
  className,
  children,
  ...props
}: MobileSheetContentProps) {
  return (
    <Drawer.Portal>
      <Drawer.Backdrop
        data-slot='mobile-sheet-backdrop'
        className={cn(
          'fixed inset-0 z-50 bg-scrim transition-opacity duration-300 ease-out',
          /* Fades with the finger as the sheet is dragged away. */
          'opacity-[calc(1-var(--drawer-swipe-progress,0))] data-swiping:duration-0',
          'data-starting-style:opacity-0 data-ending-style:opacity-0',
        )}
      />
      <Drawer.Viewport className='fixed inset-0 z-50 flex items-end'>
        <Drawer.Popup
          data-slot='mobile-sheet'
          className={cn(
            'flex max-h-[80dvh] w-full flex-col rounded-t-xl bg-surface shadow-modal outline-none',
            'pb-[env(safe-area-inset-bottom,0px)]',
            '[transform:translateY(var(--drawer-swipe-movement-y,0px))] transition-transform duration-300 ease-out',
            'data-swiping:select-none data-swiping:duration-0',
            'data-starting-style:[transform:translateY(100%)] data-ending-style:[transform:translateY(100%)]',
            className,
          )}
          {...props}
        >
          {/* The grip says the sheet can be dragged; the close button is the same door for everyone else. */}
          <div
            aria-hidden='true'
            className='mx-auto mt-2 h-1 w-9 shrink-0 rounded-full bg-line'
          />
          <div className='flex shrink-0 items-center justify-between gap-3 pt-1.5 pr-4 pb-2 pl-6'>
            <Drawer.Title
              data-slot='mobile-sheet-title'
              className='text-md font-bold text-ink'
            >
              {title}
            </Drawer.Title>
            <Drawer.Close
              data-slot='mobile-sheet-close'
              aria-label={closeLabel}
              className={cn(
                'flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-full text-ink-muted',
                'transition-colors hover:bg-fill hover:text-ink',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-faint',
              )}
            >
              <X className='size-5' aria-hidden='true' />
            </Drawer.Close>
          </div>
          <Drawer.Content
            data-slot='mobile-sheet-body'
            className='min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-4'
          >
            {children}
          </Drawer.Content>
        </Drawer.Popup>
      </Drawer.Viewport>
    </Drawer.Portal>
  );
}

export interface MobileSheetSectionProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'title'
> {
  /* Heading above this run of rows. Absent where the sheet reads as one list. */
  title?: React.ReactNode;
}

export function MobileSheetSection({
  title,
  className,
  children,
  ...props
}: MobileSheetSectionProps) {
  return (
    <div
      data-slot='mobile-sheet-section'
      className={cn('flex flex-col gap-0.5 not-first:mt-4', className)}
      {...props}
    >
      {title !== undefined && title !== null && (
        <SectionLabel as='h3' className='px-3 pb-1'>
          {title}
        </SectionLabel>
      )}
      {children}
    </div>
  );
}

/*
 * One row of the sheet. Variants because the element is the consumer's — a
 * router Link — like sidebarItemVariants. A finger's height, and the current
 * page is a green tint with the weight moving with it, never the tint alone.
 */
export const mobileSheetItemVariants = cva(
  [
    'flex min-h-11 w-full items-center rounded-md px-3 text-left text-md',
    'transition-colors',
    'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ink-faint',
  ],
  {
    variants: {
      active: {
        true: 'bg-accent-tint font-semibold text-ink',
        false: 'font-medium text-ink-secondary hover:bg-fill hover:text-ink',
      },
    },
    defaultVariants: {
      active: false,
    },
  },
);

export type MobileSheetItemVariants = VariantProps<typeof mobileSheetItemVariants>;
